import React, { useEffect, useState } from 'react';
import { apiRequest, getApiUrl } from '../../config/api';
import { timezoneOptions } from '../../utils/timezones';
import SuccessToast from '../SuccessToast';
import GoogleResourcesPanel from './GoogleResourcesPanel';
import BotCacheSection from './BotCacheSection';
import ScriptsPanel from './ScriptsPanel';
import ScheduledTasksPanel from './ScheduledTasksPanel';
import { formatoTamano } from './MediaBubble';

const mensajeDeError = (data, respaldo) => data?.error || data?.message || respaldo;

const horaEnZona = (timezone) => {
  try {
    return new Date().toLocaleString('es-ES', {
      timeZone: timezone,
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const PESTANAS = [
  { id: 'ajustes', label: 'Ajustes' },
  { id: 'script', label: 'Script' },
  { id: 'tareas', label: 'Tareas programadas' },
];

const ConfiguracionPanel = ({
  selectedBot,
  onBotUpdated,
  configTab,
  onConfigTabChange,
  appliedTaskTimezone,
  isAdmin = false,
}) => {
  const tab = configTab || 'ajustes';
  const botId = selectedBot?.botId;
  const [botName, setBotName] = useState(selectedBot?.botName || '');
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [guardandoNombre, setGuardandoNombre] = useState(false);

  useEffect(() => {
    setBotName(selectedBot?.botName || '');
  }, [selectedBot?.botName, selectedBot?.botId]);

  // Uso de la cuota de archivos del bot; null = cargando, 'error' = no disponible.
  const [usoArchivos, setUsoArchivos] = useState(null);

  useEffect(() => {
    if (!botId) return undefined;
    let cancelado = false;
    setUsoArchivos(null);
    (async () => {
      try {
        const response = await apiRequest(getApiUrl(`/bots/${botId}/media/uso`));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelado) setUsoArchivos(data);
      } catch {
        if (!cancelado) setUsoArchivos('error');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [botId]);

  if (!selectedBot) {
    return (
      <div className="configuracion-panel">
        <div className="registros-placeholder">
          <h2>Configuración</h2>
          <p>Selecciona un bot en la cabecera.</p>
        </div>
      </div>
    );
  }

  const zona = selectedBot.timezone || 'Europe/Madrid';
  const zonaFueraDeLista = !timezoneOptions.some((tz) => tz.value === zona);

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  const guardarNombre = async () => {
    if (!botId) return;
    setGuardandoNombre(true);
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/name`), {
        method: 'PATCH',
        body: JSON.stringify({ botName }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        mostrarError(mensajeDeError(data, 'No se pudo guardar el nombre'));
        return;
      }
      setBotName(data.botName);
      mostrarExito('Nombre actualizado');
      onBotUpdated?.({ botId: data.botId ?? botId, botName: data.botName });
    } catch {
      mostrarError('Error de conexión');
    } finally {
      setGuardandoNombre(false);
    }
  };

  const cambiarZona = async (timezone) => {
    if (!botId) return;
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/timezone`), {
        method: 'PATCH',
        body: JSON.stringify({ timezone }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        mostrarError(mensajeDeError(data, 'No se pudo cambiar la zona'));
        return;
      }
      mostrarExito('Zona horaria actualizada');
      onBotUpdated?.({ botId: data.botId ?? botId, timezone: data.timezone });
    } catch {
      mostrarError('Error de conexión');
    }
  };

  const usoValido = usoArchivos && usoArchivos !== 'error';
  const porcentajeUso = usoValido && usoArchivos.cuota > 0
    ? Math.min(100, Math.round((usoArchivos.bytesUsados / usoArchivos.cuota) * 100))
    : 0;

  return (
    <div className="configuracion-panel">
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      <header className="config-hero">
        <div className="config-hero-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            <path
              d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="config-hero-copy">
          <h2>Configuración</h2>
          <p>
            <strong>{selectedBot.botName}</strong>
            {' · '}
            Nombre, zona horaria, script y tareas de este bot
          </p>
        </div>
      </header>

      <div className="config-tabs" role="tablist" aria-label="Secciones de configuración">
        {PESTANAS.map((pestaña) => (
          <button
            key={pestaña.id}
            type="button"
            role="tab"
            aria-selected={tab === pestaña.id}
            className={`config-tab${tab === pestaña.id ? ' active' : ''}`}
            onClick={() => onConfigTabChange?.(pestaña.id)}
          >
            {pestaña.label}
          </button>
        ))}
      </div>

      {tab === 'ajustes' && (
        <div className="config-ajustes">
          <section className="config-card">
            <h3>Identidad</h3>
            <p className="config-card-hint">
              Así se llama el bot en la cabecera y en Administración.
            </p>
            <div className="form-group">
              <label htmlFor="config-bot-name">Nombre del bot</label>
              <div className="config-field-row">
                <input
                  id="config-bot-name"
                  type="text"
                  aria-label="Nombre del bot"
                  value={botName}
                  onChange={(e) => setBotName(e.target.value)}
                />
                <button
                  type="button"
                  className="admin-btn primary"
                  onClick={guardarNombre}
                  disabled={guardandoNombre || !botName.trim()}
                >
                  {guardandoNombre ? 'Guardando…' : 'Guardar nombre'}
                </button>
              </div>
            </div>
          </section>

          <section className="config-card">
            <h3>Zona horaria</h3>
            <p className="config-card-hint">
              Se aplica a las tareas programadas y a la hora de los mensajes en el chat.
            </p>
            <div className="form-group">
              <label htmlFor="config-bot-tz">Zona horaria</label>
              <select
                id="config-bot-tz"
                className="bm-select"
                aria-label="Zona horaria"
                value={zona}
                onChange={(e) => cambiarZona(e.target.value)}
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
                {zonaFueraDeLista ? <option value={zona}>{zona}</option> : null}
              </select>
            </div>
            <div className="config-clock">
              <span>Hora en esta zona</span>
              <strong>{horaEnZona(zona)}</strong>
            </div>
          </section>

          <section className="config-card">
            <h3>Archivos</h3>
            <p className="config-card-hint">
              Espacio usado por los archivos recibidos en este bot.
            </p>
            {usoArchivos === 'error' ? <p>No disponible</p> : null}
            {usoValido ? (
              <>
                <div
                  className="media-uso-barra"
                  role="progressbar"
                  aria-label="Uso de almacenamiento"
                  aria-valuenow={porcentajeUso}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="media-uso-relleno" style={{ width: `${porcentajeUso}%` }} />
                </div>
                <p className="media-uso-texto">
                  {formatoTamano(usoArchivos.bytesUsados)} usados de {formatoTamano(usoArchivos.cuota)}
                </p>
              </>
            ) : null}
          </section>

          {isAdmin ? <GoogleResourcesPanel botId={botId} /> : null}
          {isAdmin ? <BotCacheSection botId={botId} /> : null}
        </div>
      )}

      {tab === 'script' && (
        <div className="config-embed" data-testid="config-script">
          <ScriptsPanel
            botId={selectedBot.botId}
            botName={selectedBot.botName}
            onBotsNeedReload={() => onBotUpdated?.({ botId: selectedBot.botId })}
          />
        </div>
      )}
      {tab === 'tareas' && (
        <div className="config-embed" data-testid="config-tareas">
          <ScheduledTasksPanel
            selectedBotId={selectedBot.botId}
            selectedBotName={selectedBot.botName}
            appliedTaskTimezone={appliedTaskTimezone}
          />
        </div>
      )}
    </div>
  );
};

export default ConfiguracionPanel;
